import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  IconButton,
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
  Collapse,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Assessment as AssessmentIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { resolveApiUrl } from '../utils/api';

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  needsLocation?: boolean;
}

interface ComplianceResult {
  score: number | null;
  grade?: string;
  violations?: Array<{
    id: string;
    severity: string;
    rule: string;
    description: string;
    fix_suggestion: string;
  }>;
  suggestions?: Array<{
    priority: number;
    action: string;
    reason: string;
  }>;
  compliant_areas?: string[];
  state?: string;
  state_standard?: string;
  error?: string;
  message?: string;
}

// Updated to support multiple workzones
export interface TrafficSpecialistChatProps {
  open: boolean;
  onClose: () => void;
  signPlacement?: any;
  workzones?: any[]; // Array of workzone polygons
  variant?: 'dialog' | 'panel';
  onRequestWorkzone?: () => void; // Callback to trigger workzone drawing
  captureCanvas?: () => Promise<string | null>; // Callback to capture map canvas for vision analysis
}

export const TrafficSpecialistChat: React.FC<TrafficSpecialistChatProps> = ({
  open,
  onClose,
  signPlacement,
  workzones = [],
  variant = 'dialog',
  onRequestWorkzone,
  captureCanvas,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: `G'day! I'm Mark, your Traffic Specialist. Ask me about signs, spacing, or compliance - I'll keep it short and practical.`,
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [complianceResult, setComplianceResult] = useState<ComplianceResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showComplianceDetails, setShowComplianceDetails] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { getToken, isLoaded: authLoaded } = useClerkAuth();

  const hasWorkzone = workzones && workzones.length > 0;
  const hasAddress = workzones?.[0]?.address || workzones?.[0]?.location;
  const signCount = signPlacement?.signs?.length || 0;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Build context for API calls
  const buildContext = useCallback(() => {
    return {
      signs: signPlacement?.signs || [],
      workzones: workzones || [],
      signCount: signPlacement?.signs?.length || 0,
      workzoneCount: workzones?.length || 0,
      hasWorkzone: hasWorkzone,
      address: workzones?.[0]?.address || null,
      location: workzones?.[0]?.location || null,
      state: workzones?.[0]?.state || null,
    };
  }, [signPlacement, workzones, hasWorkzone]);

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      if (!authLoaded) {
        throw new Error('Authentication not ready');
      }

      const token = await getToken();
      const context = buildContext();

      const headers: HeadersInit = token
        ? {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }
        : { 'Content-Type': 'application/json' };

      const response = await fetch(resolveApiUrl('/api/traffic-specialist/chat'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: inputText,
          context,
          conversationHistory: messages.slice(-5).map((message) => ({
            role: message.sender === 'bot' ? 'assistant' : 'user',
            content: message.text,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response from Traffic Specialist');
      }

      const result = await response.json();

      const botMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text:
          result.response ||
          "I apologize, but I'm having trouble responding right now.",
        sender: 'bot',
        timestamp: new Date(),
        needsLocation: result.needs_location,
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text:
          "I'm sorry, I'm having technical difficulties. Please try again later.",
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalyzeCompliance = async () => {
    if (isAnalyzing) return;

    setIsAnalyzing(true);
    setComplianceResult(null);

    try {
      if (!authLoaded) {
        throw new Error('Authentication not ready');
      }

      const token = await getToken();
      const context = buildContext();

      // Capture the map canvas if available for vision-based analysis
      let capturedImage: string | null = null;
      if (captureCanvas) {
        try {
          capturedImage = await captureCanvas();
          console.log('Captured canvas for compliance analysis:', capturedImage ? 'success' : 'failed');
        } catch (e) {
          console.warn('Could not capture canvas for compliance analysis:', e);
        }
      }

      const headers: HeadersInit = token
        ? {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }
        : { 'Content-Type': 'application/json' };

      const response = await fetch(resolveApiUrl('/api/traffic-specialist/analyze'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          context,
          image: capturedImage,  // Include captured canvas for vision analysis
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze compliance');
      }

      const result = await response.json();
      setComplianceResult(result);
      setShowComplianceDetails(true);

      // Add a message about the compliance check
      if (result.score !== null) {
        const summaryText = result.summary 
          ? `\n\n${result.summary}`
          : '';
        const complianceMessage: ChatMessage = {
          id: Date.now().toString(),
          text: `I've analyzed your traffic plan. You scored ${result.score}/100 (Grade ${result.grade}) against ${result.state_standard}.${summaryText}\n\n${
            result.violations?.length > 0
              ? `Found ${result.violations.length} issue(s) to address.`
              : 'Looking good! No major issues found.'
          }`,
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, complianceMessage]);
      } else if (result.error === 'location_required') {
        const locationMessage: ChatMessage = {
          id: Date.now().toString(),
          text: result.message || 'Please set a worksite location first.',
          sender: 'bot',
          timestamp: new Date(),
          needsLocation: true,
        };
        setMessages((prev) => [...prev, locationMessage]);
      } else if (result.error === 'no_signs') {
        const noSignsMessage: ChatMessage = {
          id: Date.now().toString(),
          text: result.message || 'No signs to analyze yet.',
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, noSignsMessage]);
      }
    } catch (error) {
      console.error('Compliance analysis error:', error);
      setComplianceResult({
        score: null,
        error: 'analysis_failed',
        message: 'Failed to analyze compliance. Please try again.',
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    'What signs do I need for roadwork?',
    'Check my sign placement',
    'What spacing is required?',
    'How do I set up a lane closure?',
  ];

  const getScoreColor = (score: number | null) => {
    if (score === null) return 'grey';
    if (score >= 90) return '#4caf50';
    if (score >= 70) return '#ff9800';
    return '#f44336';
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon sx={{ color: '#f44336', fontSize: 18 }} />;
      case 'major':
        return <WarningIcon sx={{ color: '#ff9800', fontSize: 18 }} />;
      default:
        return <WarningIcon sx={{ color: '#2196f3', fontSize: 18 }} />;
    }
  };

  // Compliance Score Panel
  const compliancePanel = complianceResult && complianceResult.score !== null && (
    <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#f9f9f9', flexShrink: 0 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        onClick={() => setShowComplianceDetails(!showComplianceDetails)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: getScoreColor(complianceResult.score),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.9rem',
            }}
          >
            {complianceResult.grade}
          </Box>
          <Box>
            <Typography variant="subtitle2">Compliance Score</Typography>
            <Typography variant="caption" color="text.secondary">
              {complianceResult.score}/100 • {complianceResult.state || 'General'}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small">
          {showComplianceDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={showComplianceDetails}>
        <Box sx={{ mt: 2 }}>
          {/* Compliant areas */}
          {complianceResult.compliant_areas && complianceResult.compliant_areas.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                ✓ Compliant Areas
              </Typography>
              {complianceResult.compliant_areas.map((area, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                  <Typography variant="body2">{area}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Violations */}
          {complianceResult.violations && complianceResult.violations.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Issues Found
              </Typography>
              {complianceResult.violations.map((v, idx) => (
                <Paper key={idx} sx={{ p: 1.5, mt: 1, backgroundColor: '#fff' }} elevation={0}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {getSeverityIcon(v.severity)}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {v.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.rule}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#1976d2' }}>
                        💡 {v.fix_suggestion}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );

  // 🔹 Shared Chat UI (used in both dialog & panel modes)
  const chatUI = (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f5f5f5',
          p: 2,
          borderBottom: '1px solid #ddd',
          flexShrink: 0, // Don't shrink header
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#323232' }}>
              Traffic Specialist
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography
              variant="caption"
              sx={{
                border: '1px solid #A51211',
                borderRadius: '24px',
                px: 1.2,
                py: 0.2,
                color: '#fff',
                backgroundColor: '#A51211',
                fontSize: '0.8rem',
              }}
            >
              AI Assistant
            </Typography>
            {signCount > 0 && (
              <Chip
                size="small"
                label={`${signCount} Signs`}
                color="primary"
                variant="outlined"
              />
            )}
            {hasWorkzone ? (
              <Chip
                size="small"
                label="Workzone Set"
                color="success"
                variant="outlined"
              />
            ) : (
              <Tooltip title="Set a workzone for state-specific guidance">
                <Chip
                  size="small"
                  label="No Location"
                  color="warning"
                  variant="outlined"
                  onClick={onRequestWorkzone}
                  sx={{ cursor: onRequestWorkzone ? 'pointer' : 'default' }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Compliance Check Button */}
          <Tooltip title="Check compliance against standards">
            <IconButton
              onClick={handleAnalyzeCompliance}
              disabled={isAnalyzing}
              size="small"
              sx={{
                backgroundColor: '#f0f0f0',
                '&:hover': { backgroundColor: '#e0e0e0' },
              }}
            >
              {isAnalyzing ? (
                <CircularProgress size={20} />
              ) : (
                <AssessmentIcon sx={{ color: '#A51211' }} />
              )}
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Compliance Panel */}
      {compliancePanel}

      {/* Location Hint Banner */}
      {!hasWorkzone && messages.some(m => m.needsLocation) && (
        <Alert
          severity="info"
          sx={{ mx: 2, mt: 1, borderRadius: 2, flexShrink: 0 }}
          action={
            onRequestWorkzone && (
              <Button color="inherit" size="small" onClick={onRequestWorkzone}>
                Set Location
              </Button>
            )
          }
        >
          Set a worksite location for state-specific guidance
        </Alert>
      )}

      {/* Messages - scrollable area */}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          maxHeight: 'calc(80vh - 250px)', // Force max height: dialog height minus header/input/padding
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 2,
          backgroundColor: '#fafafa',
        }}
      >
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent:
                message.sender === 'user' ? 'flex-end' : 'flex-start',
              mb: 2,
            }}
          >
            <Paper
              sx={{
                p: 2,
                maxWidth: '100%',
                backgroundColor:
                  message.sender === 'user' ? '#1976d2' : '#ffffff',
                color: message.sender === 'user' ? 'white' : 'black',
                borderRadius:
                  message.sender === 'user'
                    ? '16px 16px 4px 16px'
                    : '16px 16px 16px 4px',
              }}
            >
              {message.sender === 'bot' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    component="img"
                    src="/images/mark-avatar.jpg"
                    alt="Mark"
                    onError={(e: any) => {
                      e.target.style.display = 'none';
                    }}
                    sx={{
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      mr: 1,
                      border: '2px solid #EAE8EA',
                    }}
                  />
                  <Typography variant="caption" color="#393939">
                    Mark <br /> Traffic Specialist
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.text}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1,
                  opacity: 0.7,
                  fontSize: '0.7rem',
                }}
              >
                {message.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            </Paper>
          </Box>
        ))}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
              <Typography variant="body2" color="text.secondary">
                Mark is thinking...
              </Typography>
            </Paper>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Quick Actions */}
      {messages.length <= 1 && (
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Quick Actions:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              flexDirection: 'column',
              width: 'fit-content',
            }}
          >
            {quickActions.map((action, index) => (
              <Chip
                key={index}
                label={action}
                variant="outlined"
                size="small"
                onClick={() => setInputText(action)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '16px',
                  borderColor: '#A51211',
                  color: '#A51211',
                  width: 'fit-content',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Input */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#ffffff',
          flexShrink: 0, // Don't shrink input area
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me about traffic management..."
            variant="outlined"
            size="small"
            disabled={isLoading}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isLoading}
            color="primary"
            sx={{ mb: 0.5 }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  if (!open) return null;

  // 🔹 Variant switch
  if (variant === 'panel') {
    return <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{chatUI}</Box>;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      scroll="paper"
      PaperProps={{
        sx: {
          width: '450px',
          height: '80vh',
          maxHeight: '850px',
          borderRadius: 5,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          // Prevent MUI from auto-sizing
          '&.MuiDialog-paper': {
            height: '80vh',
            maxHeight: '850px',
          }
        },
      }}
    >
      {chatUI}
    </Dialog>
  );
};
